/** @file This file contains the store for site settings. */
import moment from 'moment';
import * as api from '@codegrade/api';
import { Just, Maybe, Nothing } from '@codegrade/maybe';
import * as siteSettings from '@codegrade/site-settings';
import { object } from '@codegrade/utils';
import { getStoreBuilder } from '@codegrade/vuex-typex';
import client, { unauthorizedClient } from '@/client';
import * as utils from '@/utils';
import { UserStore } from './user';
const storeBuilder = getStoreBuilder();
/**
 * A function to return the initial state of the store module.
 *
 * @returns The initial, empty state of the store module.
 */
const makeInitialState = () => ({
    siteSettings: Maybe.empty(),
    siteLoader: Maybe.empty(),
    serverRelease: undefined,
    serverTimeOffset: Nothing,
    tenantSettings: {},
    tenantLoaders: {},
});
const moduleBuilder = storeBuilder.module('siteSettings', makeInitialState());
/** The SiteSettings store module. */
export var SiteSettingsStore;
(function (SiteSettingsStore) {
    /**
     * Get a function that maybe retrieves a site setting, returning `Nothing` if it couldn't be
     * found.
     *
     * @param state The state to retrieve the settings from.
     * @returns A function that given a site setting and the tenant id retrieves a site settings,
     * returning `Nothing` if it couldn't be found.
     */
    function maybeGetSettingFunc(state) {
        return (opt, tenantId) => Maybe.fromNullable(state.tenantSettings[tenantId]).chainNullable(object.attr(opt));
    }
    /**
     * Get a function to get a particular setting from the given state of the store module.
     *
     * @param state The state to extract the setting from.
     *
     * @returns A function that returns the site setting for a tenant.
     */
    // eslint-disable-next-line no-underscore-dangle
    function _getSetting(state) {
        const maybeGetter = maybeGetSettingFunc(state);
        return (opt, tenantId) => maybeGetter(opt, tenantId)
            .ifNothing(() => {
            utils.loadBackgroundPromises(
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            SiteSettingsStore.loadTenantSettings({
                force: false,
                catchAllErrors: true,
                tenantId,
            }));
        })
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            .orDefaultLazy(() => _getGlobalSetting(state)(opt));
    }
    /**
     * A getter to maybe retrieve a site setting, this will return `Nothing` while the setting is
     * not loaded yet.
     *
     * This function will not load the setting if it is not loaded yet.
     */
    SiteSettingsStore.maybeGetSetting = moduleBuilder.read(maybeGetSettingFunc, 'maybeGetSetting');
    /**
     * A getter to retrieve a site setting for a specific tenant.
     *
     * When the settings are not loaded yet we will fall back to global settings, kick off an
     * action to load the tenant settings, and return the global value in the meantime.
     */
    SiteSettingsStore.getSetting = moduleBuilder.read(_getSetting, 'getSetting');
    /**
     * Create a setting getter bound to a specific tenant id.
     *
     * Convenience for components that know their tenant and want to pass a
     * simple getter to children.
     *
     * @param tid The tenant id to bind.
     * @returns A function that retrieves settings for the given tenant.
     */
    function getSettingForTenant(tid) {
        const getter = SiteSettingsStore.getSetting();
        return opt => getter(opt, tid);
    }
    SiteSettingsStore.getSettingForTenant = getSettingForTenant;
    /**
     * Get a function to retrieve a global site setting (not tenant-specific).
     *
     * Use this when no tenant context is available (e.g., before the user is
     * logged in). When the settings are not loaded yet we will kick off an
     * action to load them and return the default value.
     *
     * @param state The state to extract the setting from.
     * @returns A function that returns the global site setting.
     */
    // eslint-disable-next-line no-underscore-dangle
    function _getGlobalSetting(state) {
        return (opt) => state.siteSettings
            .chainNullable(object.attr(opt))
            .ifNothing(() => {
            utils.loadBackgroundPromises(
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            SiteSettingsStore.loadSiteSettings({
                force: false,
                catchAllErrors: true,
            }));
        })
            .orDefaultLazy(() => siteSettings.FRONTEND_SETTINGS_DEFAULTS[opt]);
    }
    /** A getter to retrieve a global site setting (not tenant-specific). */
    SiteSettingsStore.getGlobalSetting = moduleBuilder.read(_getGlobalSetting, 'getGlobalSetting');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    SiteSettingsStore.releaseInfo = moduleBuilder.read(state => Maybe.fromNullable(state.serverRelease).filter(info => info.commit === COMMIT_HASH), 'releaseInfo');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    SiteSettingsStore.getServerTimeOffset = moduleBuilder.read(state => state.serverTimeOffset, 'getServerTimeOffset');
    /** @returns A getter to get the current time potentially corrected for clock drift. */
    SiteSettingsStore.currentTimeGetter = moduleBuilder.read(() => {
        const correction = SiteSettingsStore.getServerTimeOffset().filter(offset => {
            const diffTolerance = moment.duration(5, 'minutes');
            return Math.abs(offset.asSeconds()) > diffTolerance.asSeconds();
        });
        return () => utils.datetime.momentUtc(correction);
    }, 'currentTimeGetter');
    const removeLoader = moduleBuilder.commit(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    (state, { tenantId }) => {
        tenantId.caseOf({
            Just: t => {
                utils.vueDelete(state.tenantLoaders, t);
            },
            Nothing: () => {
                utils.vueSet(state, 'siteLoader', Maybe.empty());
            },
        });
    }, 'removeLoader');
    const commitLoader = moduleBuilder.commit((state, { tenantId, loader }) => {
        loader.catch(() => removeLoader({ tenantId }));
        tenantId.caseOf({
            Just: t => {
                utils.vueSet(state.tenantLoaders, t, loader);
            },
            Nothing: () => {
                utils.vueSet(state, 'siteLoader', Maybe.of(loader));
            },
        });
    }, 'commitLoader');
    const commitServerRelease = moduleBuilder.commit((state, { version, date, message, uiPreference, commit }) => {
        if (version == null || date == null || message == null || uiPreference == null) {
            state.serverRelease = {
                commit,
            };
        }
        else {
            state.serverRelease = {
                version,
                message,
                commit,
                date,
                uiPreference,
            };
        }
    }, 'commitServerCommit');
    const commitServerTime = moduleBuilder.commit((state, data) => {
        state.serverTimeOffset = new Just(moment.duration(data.diff(utils.datetime.momentUtc(Maybe.empty()))));
    }, 'commitServerTime');
    const clearTenantSettings = moduleBuilder.commit(state => {
        state.tenantSettings = {};
        state.tenantLoaders = {};
    }, 'clearTenantSettings');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    SiteSettingsStore.commitSettings = moduleBuilder.commit((state, { tenantId, settings, }) => {
        tenantId.caseOf({
            Just: t => {
                utils.vueSet(state.tenantSettings, t, settings);
            },
            Nothing: () => {
                utils.vueSet(state, 'siteSettings', Maybe.of(settings));
            },
        });
    }, 'commitSettings');
    const patchSettings = moduleBuilder.commit((state, { tenantId, settings, }) => {
        tenantId.caseOf({
            Just: t => {
                const oldSettings = state.tenantSettings[t] ?? {};
                utils.vueSet(state.tenantSettings, t, { ...oldSettings, ...settings });
            },
            Nothing: () => {
                state.siteSettings.ifJust(oldSettings => {
                    utils.vueSet(state, 'siteSettings', Maybe.of({ ...oldSettings, ...settings }));
                    clearTenantSettings();
                });
            },
        });
    }, 'patchSettings');
    /** Action to load the general site settings into the store. */
    SiteSettingsStore.loadSiteSettings = moduleBuilder.dispatch(async ({ state }, payload = {}) => {
        const { force = false, forLoggedInUser = false, catchAllErrors = false } = payload;
        const tenantId = UserStore.loggedInUser()
            .filter(() => forLoggedInUser)
            .map(u => u.tenantId);
        return await utils.loadApiWithMaybeForce({
            force,
            value: state.siteSettings,
            loader: state.siteLoader,
            load: async () => {
                try {
                    const response = await unauthorizedClient.p.about.get({
                        tenantId: tenantId.extract(),
                    });
                    const { cgResult } = response;
                    commitServerTime(cgResult.currentTime);
                    commitServerRelease(cgResult.release);
                    SiteSettingsStore.commitSettings({
                        settings: siteSettings.frontendWithDefaults(cgResult.settings),
                        tenantId,
                    });
                    return response;
                }
                catch (err) {
                    if (catchAllErrors) {
                        return api.ApiResult.fromResponseAndValue(new Response(), null);
                    }
                    throw err;
                }
            },
            addLoader: loader => {
                commitLoader({
                    tenantId: Maybe.empty(),
                    loader,
                });
                tenantId.ifJust(() => {
                    commitLoader({ tenantId, loader });
                });
            },
            removeLoader: () => {
                removeLoader({ tenantId: Maybe.empty() });
                tenantId.ifJust(() => {
                    removeLoader({ tenantId });
                });
            },
        });
    }, 'loadSiteSettings');
    /** Action to load the site settings of a specific tenant into the store. */
    SiteSettingsStore.loadTenantSettings = moduleBuilder.dispatch(async ({ state }, payload) => {
        const { tenantId, force = false, catchAllErrors = false } = payload;
        return await utils.loadApiWithMaybeForce({
            force,
            value: Maybe.fromNullable(state.tenantSettings[tenantId]),
            loader: Maybe.fromNullable(state.tenantLoaders[tenantId]),
            load: async () => {
                try {
                    const response = await unauthorizedClient.p.tenant.getSettings({
                        tenantId,
                        onlyFrontend: true,
                    });
                    const encoded = api.AllSiteSettings.encode(response.cgResult);
                    api.FrontendSiteSettings.decode(encoded)
                        .mapLeft(Error)
                        .ifRight(cgResult => {
                        SiteSettingsStore.commitSettings({
                            tenantId: Maybe.of(tenantId),
                            settings: siteSettings.frontendWithDefaults(cgResult),
                        });
                    });
                    return response.map(() => encoded);
                }
                catch (err) {
                    if (catchAllErrors) {
                        return api.ApiResult.fromResponseAndValue(new Response(), null);
                    }
                    throw err;
                }
            },
            addLoader: loader => commitLoader({
                tenantId: Maybe.of(tenantId),
                loader,
            }),
            removeLoader: () => removeLoader({ tenantId: Maybe.of(tenantId) }),
        });
    }, 'loadTenantSettings');
    /** Action to update site settings, either the global one or the one of a tenant. */
    SiteSettingsStore.updateSettings = moduleBuilder.dispatch(async (_, payload) => {
        const { tenantId, data } = payload;
        const requestBody = { updates: data };
        /**
         * This function updates tenant site settings in the backend.
         *
         * @param tId The tenant id to patch settings for.
         * @returns An api result containing the updated settings or an error.
         */
        const patchTenantSettings = async (tId) => await client.p.tenant.patchSettings({ tenantId: tId, requestBody });
        const resp = await tenantId.map(patchTenantSettings).orDefaultLazy(async () => {
            // Invalidate all tenant settings because they fall back
            // to the global site settings which have just changed.
            clearTenantSettings();
            return await client.p.siteSettings.patch({ requestBody });
        });
        patchSettings({ tenantId, settings: resp.cgResult });
        return resp;
    }, 'updateSettings');
    utils.signal.AFTER_USER_CHANGED.register(async (user) => {
        if (user.isJust()) {
            await SiteSettingsStore.loadSiteSettings({ forLoggedInUser: true });
            // We do this here to ensure we load the settings for this tenant.
            // This is useful if we go from a user in tenant_a to a user in tenant_b. In this
            // case `loadSiteSettings` for the current user won't do anything but we won't have
            // the site settings for the tenant loaded.
            // We load them in sequence to prevent double loading.
            await SiteSettingsStore.loadTenantSettings({ tenantId: user.value.tenantId });
        }
    });
})(SiteSettingsStore || (SiteSettingsStore = {}));
